import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '@ofself/shell';
import { Plus, Minus, Lock } from 'lucide-react';
import api from '../../services/api';
import {
    loadSchemaInfo, tintOf, schemaOf, titleOf, previewOf, rowOf, rowForProperty, valueOfRow, missingRequired,
} from './GraphModel';
import { whenOf } from '../../nucleus/format';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { Row } from '../../components/nucleus/Collection';
import { NodeMark } from '../../components/nucleus/faces';
import Picker from '../../components/nucleus/Picker';

// THE GRAPH'S SHEETS (parity with nucleus.oS GraphMore.swift + GraphEdit.swift):
// the graph as it was, saving and renaming a view, and the node editor.
// Each is the shell's Modal with the Mac's words.

export function SheetLabel({ children }) {
    return <div className="graph-sheet__label">{children}</div>;
}

function localInput(d) {
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Your graph as it was. Personal only: /graph/snapshot has no group branch. */
export function GraphTimeTravelSheet({ onClose }) {
    const [at, setAt] = useState(() => localInput(new Date(Date.now() - 7 * 86400000)));
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const show = async () => {
        setLoading(true); setError(null);
        try {
            const r = await api.getGraphSnapshot(new Date(at).toISOString());
            setResult({ nodes: r?.nodes || [], relationships: (r?.relationships || []).length, at: new Date(at) });
        } catch (e) { setError(e?.message || 'Couldn’t look back that far.'); }
        setLoading(false);
    };
    return (
        <Modal title="Your graph as it was" onClose={onClose}>
            <div className="graph-sheet">
                <div className="graph-sheet__row">
                    <input type="datetime-local" className="graph-input" value={at} max={localInput(new Date())} onChange={(e) => setAt(e.target.value)} />
                    <FootButton onClick={show} busy={loading}>Show</FootButton>
                </div>
                {error && <p className="graph-sheet__error">{error}</p>}
                {result && (
                    <>
                        <p className="graph-sheet__lead">
                            {result.nodes.length} nodes, {result.relationships} relationships on {whenOf(result.at, { year: true })}
                        </p>
                        <div className="graph-snapshot nx-rows">
                            {result.nodes.map((n) => (
                                <Row key={n.id} lead={<NodeMark schema={schemaOf(n)} schemaId={n.schema_id} hue={tintOf(schemaOf(n))} />}
                                    name={titleOf(n)} sub={previewOf(n) || undefined} end={schemaOf(n)} />
                            ))}
                        </div>
                    </>
                )}
                <SheetFoot><FootButton primary onClick={onClose}>Done</FootButton></SheetFoot>
            </div>
        </Modal>
    );
}

/** Save this view — optionally inside another one (prod's child views). */
export function GraphSaveViewSheet({ views = [], inside: insideFirst = null, onSave, onClose }) {
    const [name, setName] = useState('');
    const [inside, setInside] = useState(insideFirst || '');
    const parents = views.filter((v) => !v.settings?.parentDatabaseId);
    return (
        <Modal title="Save this view" onClose={onClose} size="sm">
            <form className="graph-sheet" onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSave(name.trim(), inside || null); }}>
                <p className="graph-sheet__lead">Keeps the kind, search, sort and filter you’re looking at now.</p>
                <input autoFocus className="graph-input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
                {parents.length > 0 && (
                    <Picker label="Inside" value={inside} onChange={setInside}
                        options={[{ value: '', label: 'On its own' }, ...parents.map((v) => ({ value: v.id, label: `Inside “${v.name}”` }))]} />
                )}
                <SheetFoot>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    <FootButton primary type="submit" disabled={!name.trim()}>Save</FootButton>
                </SheetFoot>
            </form>
        </Modal>
    );
}

export function GraphRenameSheet({ title = 'Rename view', name: first = '', onSave, onClose }) {
    const [name, setName] = useState(first);
    return (
        <Modal title={title} onClose={onClose} size="sm">
            <form className="graph-sheet" onSubmit={(e) => { e.preventDefault(); if (name.trim()) onSave(name.trim()); }}>
                <input autoFocus className="graph-input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
                <SheetFoot>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    <FootButton primary type="submit" disabled={!name.trim()}>Rename</FootButton>
                </SheetFoot>
            </form>
        </Modal>
    );
}

/** Edit a node: its title, its kind, every field (parity with NodeEditor).
 *  Each field keeps its type: text stays text ("123" is not a number), a list
 *  stays a list. Aa / { } beside a field says which it is, and switches it.
 *  `onSave({ title, schemaId, value })` writes it (GraphModel.saveNode). */
export function GraphNodeEditor({ node, group, sealedFrom = {}, onSave, onClose }) {
    const original = node?.value_json || {};
    const [title, setTitle] = useState(node?.title || '');
    const [schemaId, setSchemaId] = useState(node?.schema_id || '');
    const [schemas, setSchemas] = useState([]);
    const [info, setInfo] = useState(null);
    const [rows, setRows] = useState(() => Object.keys(original).sort().map((k) => rowOf(k, original[k])));
    const [working, setWorking] = useState(false);
    const [error, setError] = useState(null);
    // the group route has no schema change, and no route returns a node to freeform
    const kindLocked = !!(group && node?.id);

    useEffect(() => {
        // the kinds usable in this space: in a group, the group's view
        api.getSchemas({ include_json_schema: 'false', limit: 1000, ...(group ? { group_id: group } : {}) })
            .then((s) => setSchemas([...s].sort((a, b) => String(a.schema_name).localeCompare(String(b.schema_name)))))
            .catch(() => setSchemas([]));
    }, [group]);
    useEffect(() => {
        if (!schemaId) { setInfo(null); return undefined; }
        let live = true;
        loadSchemaInfo(schemaId).then((s) => {
            if (!live) return;
            setInfo(s);
            setRows((rs) => [...rs, ...Object.keys(s.properties).sort().filter((p) => !rs.some((r) => r.key === p)).map((p) => rowForProperty(p, s.properties[p]))]);
        }).catch(() => {});
        return () => { live = false; };
    }, [schemaId]);

    const set = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const save = async () => {
        setError(null);
        const value = {};
        try {
            for (const r of rows) {
                const k = r.key.trim();
                if (!k) continue;
                if (r.sealed) { value[k] = sealedFrom[k] ?? original[k]; continue; }   // keep ciphertext you couldn't open
                const v = valueOfRow(r, original);
                if (v !== undefined) value[k] = v;
            }
        } catch (e) { setError(e.message); return; }
        // REQUIRED FIELDS, before anything is sent: the server would refuse it anyway,
        // and a refusal after the round trip says less than naming the fields here
        const missing = missingRequired(info, value);
        if (missing.length) {
            const said = missing.map((k) => `“${k}”`).join(', ');
            setError(`${said} ${missing.length === 1 ? 'is' : 'are'} required by the ${info.name || 'chosen'} schema. Fill ${missing.length === 1 ? 'it' : 'them'} in to save.`);
            return;
        }
        setWorking(true);
        try { await onSave({ title, schemaId: schemaId || null, value }); } catch (e) { setError(e?.message || 'Couldn’t save it.'); }
        setWorking(false);
    };
    const kinds = useMemo(() => schemas.filter((s) => s.id), [schemas]);

    return (
        <Modal title={node?.id ? 'Edit node' : 'New node'} onClose={onClose}>
            <div className="graph-sheet graph-editor">
                <SheetLabel>Title</SheetLabel>
                <input className="graph-input is-title" placeholder="Title" aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
                <SheetLabel>Schema</SheetLabel>
                <Picker label="Schema" value={schemaId || ''} disabled={kindLocked} onChange={setSchemaId}
                    options={[{ value: '', label: 'Freeform', disabled: !!node?.schema_id }, ...kinds.map((s) => ({ value: s.id, label: s.schema_name }))]} />
                {kindLocked && <span className="graph-quiet-inline">A group’s node keeps the kind it was made with.</span>}
                <SheetLabel>Fields</SheetLabel>
                <div className="graph-editor__fields">
                    {rows.map((r) => (
                        <div key={r.id} className="graph-editor__field">
                            <input className="graph-input is-mono graph-editor__key" placeholder="field" aria-label="Field name" value={r.key} onChange={(e) => set(r.id, { key: e.target.value })} />
                            {r.sealed
                                ? <span className="graph-editor__sealed">encrypted — unlock your key to edit</span>
                                : <textarea className={`graph-input graph-editor__value${r.kind === 'json' ? ' is-mono' : ''}`} rows={Math.min(8, Math.max(1, r.text.split('\n').length))}
                                    placeholder={r.kind === 'json' ? 'JSON — 3, true, ["a","b"]' : 'value'} aria-label={`${r.key || 'field'} value`}
                                    value={r.text} onChange={(e) => set(r.id, { text: e.target.value })} />}
                            {!r.sealed && (
                                <button type="button" className="graph-editor__kind" aria-label={r.kind === 'json' ? `${r.key || 'field'} is JSON — make it text` : `${r.key || 'field'} is text — make it JSON`}
                                    title={r.kind === 'json' ? 'JSON: a number, true/false, a list or an object. Press for text.' : 'Text, kept exactly. Press for JSON.'}
                                    onClick={() => set(r.id, { kind: r.kind === 'json' ? 'text' : 'json' })}>
                                    {r.kind === 'json' ? '{ }' : 'Aa'}
                                </button>
                            )}
                            {info?.encrypted?.includes(r.key) && <Lock size={10} className="graph-editor__lock" aria-label="encrypted field" />}
                            <button type="button" className="graph-editor__drop" aria-label={`Remove ${r.key || 'field'}`} onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}>
                                <Minus size={12} />
                            </button>
                        </div>
                    ))}
                    <button type="button" className="graph-link graph-editor__add" onClick={() => setRows((rs) => [...rs, rowOf('', undefined, { known: false })])}>
                        <Plus size={11} /> Add a field
                    </button>
                </div>
                {error && <p className="graph-sheet__error" role="alert">{error}</p>}
                <SheetFoot>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    <FootButton primary disabled={!title} busy={working} onClick={save}>Save</FootButton>
                </SheetFoot>
            </div>
        </Modal>
    );
}
